<template>
  <div class="admin-videos">
    <admin-view-header title="Filmy">
      <div class="field is-fullwidth has-addons">
        <div
          class="control is-expanded has-icons has-icons-left"
          :class="{ 'is-loading': searching }"
        >
          <input
            type="text"
            class="input"
            placeholder="Wyszukaj materiały filmowe..."
            v-model.trim="query"
          />
          <icon name="fa-search" class="is-left" />
        </div>

        <div class="control" v-if="query.length > 0">
          <button
            class="button has-tooltip-bottom"
            @click="clearQuery()"
            data-tooltip="Wyczyść"
          >
            <icon name="fa-trash" />
          </button>
        </div>
      </div>

      <protected
        :with="[Permission.CREATE_VIDEO, Permission.READ_VIDEO_SOURCES]"
      >
        <button class="button is-success" @click="openNewVideoModal">
          <icon name="fa-plus" />
          <span>Nowy film</span>
        </button>
      </protected>
    </admin-view-header>

    <message-box class="is-info" title="Informacja" icon="fa-info">
      <div>
        Materiały filmowe mogą być wyszukiwane za pomocą paska wyszukiwania
        umieszczonego zarówno na niniejszej stronie jak i na
        <router-link to="/videos"
          >podstronie z archiwalnymi materiałami TV</router-link
        >. Ze względów technicznych jednak, wszelkie operacje na filmach (tj.
        dodawanie, modyfikowanie oraz usuwanie) propagowane są do mechanizmu
        wyszukiwania w <strong>15</strong>-minutowych interwałach.
      </div>
      <div>
        Oznacza to, że:
        <ul>
          <li>
            nowoutworzony materiał filmowy będzie dostępny w wyszukiwarce po
            <strong>15</strong> minutach,
          </li>
          <li>
            modyfikacja istniejącego materiału zostanie uwzględniona w
            wyszukiwarce po
            <strong>15</strong> minutach,
          </li>
          <li>
            usunięty materiał będzie przez
            <strong>15</strong> minut wciąż dostępny w wyszukiwarce.
          </li>
        </ul>
      </div>
    </message-box>

    <loader :state="actions.videos" without-loading>
      <template>
        <table class="table is-fullwidth">
          <thead>
            <tr>
              <th>Tytuł</th>
              <th>Opublikowano</th>
              <th>Akcja</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="video in videos" :key="video.id">
              <td>
                <router-link :to="`/videos/${video.id}`">
                  {{ video.title }}
                </router-link>
              </td>
              <td class="nowrap">{{ video.createdDate }}</td>
              <td class="nowrap">
                <protected :with="Permission.READ_STATISTICS">
                  <icon
                    class="icon-control"
                    name="fa-chart-area"
                    data-tooltip="Statystyki filmu"
                    @click="showVideoStatistics(video)"
                  />
                </protected>
                <protected
                  :with="[
                    Permission.UPDATE_VIDEO,
                    Permission.READ_VIDEO_SOURCES
                  ]"
                >
                  <icon
                    class="icon-control"
                    name="fa-pen"
                    data-tooltip="Edytuj film"
                    @click="openEditVideoModal(video)"
                  />
                </protected>
                <protected :with="Permission.DELETE_VIDEO">
                  <icon
                    class="icon-control is-danger"
                    name="fa-trash"
                    data-tooltip="Usuń film"
                    @click="showRemovalConfirmation(video)"
                  />
                </protected>
              </td>
            </tr>
          </tbody>
        </table>

        <pagination
          :pageable="pageable"
          @change-page="changePage"
          :delta="1"
          buttons
        />
      </template>

      <template #error>
        <div class="grey-message">
          <icon name="fa-times-circle" />
          <p>Dane niedostępne z powodu braku połączenia z serwerem.</p>
        </div>
      </template>
    </loader>
  </div>
</template>

<script>
import { mapState } from "vuex";
import {
  FETCH_VIDEOS,
  FETCH_VIDEO,
  ADD_VIDEO,
  UPDATE_VIDEO,
  DELETE_VIDEO
} from ":actions";

import dayjs from "dayjs";

import { Icon, Pagination, MessageBox } from "%helpers";

import AdminViewHeader from "%admin/components/global/AdminViewHeader";

import { Protected, WithDispatcherProgress } from "%mixins";

import { VideoModal, YesNoCancelModal } from "%modals";
import VideoStatisticsModal from "%modals/VideoStatisticsModal";

import { SuccessNotification } from "%notifications";

import { omit } from "%utils/object";

import { debounce } from "%utils/functions";

export default {
  name: "admin-videos",

  components: {
    Icon,
    AdminViewHeader,
    Pagination,
    MessageBox
  },

  mixins: [Protected, WithDispatcherProgress],

  data() {
    return {
      page: 1,
      query: "",
      searching: false
    };
  },

  computed: mapState({
    videos: state =>
      state.videos.videos.content.map(video => ({
        ...video,
        createdDate: dayjs(video.createdDate).format("YYYY-MM-DD, HH:mm")
      })),

    pageable: state => omit(state.videos.videos, "content")
  }),

  mounted() {
    if (this.videoId) {
      this.openEditVideoModal(this.videoId);
    }
  },

  methods: {
    openNewVideoModal() {
      this.$emit("show-modal", {
        component: VideoModal,
        props: {
          onSubmit: video => this.addVideo(video)
        }
      });
    },

    addVideo(video) {
      return this.$store
        .dispatch(ADD_VIDEO, { video })
        .then(() => {
          this.query = "";
        })
        .then(() =>
          this.successNotification(
            `Film <strong>${video.title}</strong> został utworzony.`
          )
        );
    },

    successNotification(content) {
      this.$emit("push-notification", {
        component: SuccessNotification,
        props: {
          content
        }
      });
    },

    openEditVideoModal({ id }) {
      this.$store.dispatch(FETCH_VIDEO, { id }).then(({ data }) =>
        this.$emit("show-modal", {
          component: VideoModal,
          props: {
            video: data,
            onSubmit: video => this.updateVideo(id, data.title, video)
          }
        })
      );
    },

    updateVideo(id, title, video) {
      return this.$store
        .dispatch(UPDATE_VIDEO, { id, video })
        .then(() => {
          this.query = "";
        })
        .then(() =>
          this.successNotification(
            `Film <strong>${video.title ||
              title}</strong> został zaktualizowany.`
          )
        );
    },

    clearQuery() {
      this.query = "";
    },

    debouncedSearch: debounce(function() {
      this.fetchVideos();
    }, 500),

    fetchVideos() {
      this.dispatchWithProgress("videos", FETCH_VIDEOS, {
        page: this.page,
        size: 10,
        query: this.query === "" ? undefined : this.query
      }).then(() => (this.searching = false));
    },

    changePage(page) {
      this.page = page;
    },

    showRemovalConfirmation({ id, title }) {
      this.$emit("show-modal", {
        component: YesNoCancelModal,
        props: {
          title: "Potwierdzenie usunięcia",
          content: `
            <p>Czy na pewno chcesz usunąć film o poniższym tytule?</p>
            <blockquote>${title}</blockquote>                       
          `,
          proceed: {
            type: "is-danger",
            icon: "fa-trash",
            content: "Usuń",
            callback: () => this.removeVideo(id)
          },
          cancel: {
            content: "Anuluj"
          }
        }
      });
    },

    removeVideo(id) {
      return this.$store.dispatch(DELETE_VIDEO, { id }).then(() => {
        this.query = "";
      });
    },

    showVideoStatistics({ id, title }) {
      this.$emit("show-modal", {
        component: VideoStatisticsModal,
        props: {
          video: { id, title }
        }
      });
    }
  },

  beforeMount() {
    this.fetchVideos();
  },

  watch: {
    query() {
      this.searching = true;
      this.debouncedSearch();
    },

    page() {
      this.fetchVideos();
    }
  }
};
</script>

<style lang="scss" scoped>
.message {
  margin-top: 3rem;
}
</style>
